'use client';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Hooks from '@benzinga/hooks';
import { isMobile } from '@benzinga/device-utils';
import { formatTickerToURLFriendly } from '@benzinga/utils';
import { Ticker } from '@benzinga/session';
import { AutocompleteResults } from './AutocompleteResults';
import { SearchBar } from './SearchBar';
import { FallbackLogoSVG } from './FallbackLogoSVG';
import { TrendingSections } from './TrendingSections';
import { useTrending } from './useTrending';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useRouter } from 'next/navigation';
import { SessionContext } from '@benzinga/session-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { BasicNewsManager } from '@benzinga/basic-news-manager';
import type { SearchResultItem, SearchResultSymbolItem } from '@benzinga/basic-news-manager';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import styles from './Search.module.scss';

export type SearchType = 'all' | 'ticker' | 'article' | 'keyword';
export type SearchVariant = 'default' | 'box' | 'navbar' | 'inline';

const MAX_SEARCH_RESULTS_DISPLAY = 5;

const MIN_ARTICLE_SEARCH_QUERY_LENGTH = 3;

export interface SearchProps {
  autoFocus?: boolean;
  handleRemove?: (removed: Ticker) => void;
  initialTrendingTickers?: Ticker[];
  initialTrendingTopics?: TrendingTopic[];
  onSearching?: (boolean: boolean) => void;
  onSelect?: (selected: Ticker) => void;
  onToggle?: (value?: boolean) => void;
  placeholder?: string;
  query?: string;
  searchType?: SearchType;
  selected?: Ticker[] | Ticker | null;
  showBackdrop?: boolean;
  showTrending?: boolean;
  variant?: SearchVariant;
  isFullScreenSearch?: boolean;
  dropdownSuppressed?: boolean;
  onActivate?: () => void;
}

const selectElementListClass = 'search-list-item';

type SelectableItem = { type: 'ticker'; ticker: Ticker } | { type: 'link'; href: string };

const isAbsoluteUrl = (href: string) => /^https?:\/\//i.test(href);

export const Search: React.FC<SearchProps> = ({
  autoFocus,
  handleRemove,
  initialTrendingTickers,
  initialTrendingTopics,
  isFullScreenSearch = false,
  onSearching,
  onSelect,
  onToggle,
  placeholder = 'Tickers, Articles or Keywords',
  query,
  searchType = 'all',
  selected,
  showBackdrop = false,
  showTrending = false,
  variant = 'default',
  dropdownSuppressed = false,
  onActivate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const latestQueryRef = useRef('');
  const [foundedTickers, setFoundedTickers] = useState<Ticker[]>([]);
  const [foundedArticles, setFoundedArticles] = useState<SearchResultItem[]>([]);
  const [fetchDebounce, setFetchDebounce] = useState<number>();
  const [isFetching, setIsFetching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState<boolean>(true);

  const [hasOpened, setHasOpened] = useState<boolean>(false);
  const [searchResultsHeight, setSearchResultsHeight] = useState<string>('auto');
  const [pendingEnter, setPendingEnter] = useState<boolean>(false);

  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const trendingTrackedRef = useRef<boolean>(false);

  const {
    ensureLoaded,
    isLoading: trendingLoading,
    trendingTickers,
    trendingTopics,
  } = useTrending(initialTrendingTopics, initialTrendingTickers);
  const hasTrending = showTrending && (trendingLoading || trendingTickers.length > 0 || trendingTopics.length > 0);

  const articlesSearch = searchType === 'all' || searchType === 'article';
  const keywordsSearch = searchType === 'all' || searchType === 'keyword';
  const tickersSearch = searchType === 'all' || searchType === 'ticker';

  const router = useRouter();
  const ref = useRef<HTMLDivElement | null>(null);

  const session = React.useContext(SessionContext);

  Hooks.useEventListener('resize', () => {
    if (isMobile()) {
      const windowHeightSeventyPercent = `${Math.round(window.innerHeight * 0.7)}px`;
      setSearchResultsHeight(windowHeightSeventyPercent);
    }
  });

  Hooks.useClickOutside(ref, () => {
    setSelectedIndex(-1);
    setShowDropdown(false);
  });

  useEffect(() => {
    if (query !== undefined) {
      setSearchQuery(query);
    }
  }, [query]);

  useEffect(() => {
    if (!searchQuery) setSelectedIndex(-1);
  }, [searchQuery]);

  const trendingVisible = hasOpened && !searchQuery && showDropdown && hasTrending;
  useEffect(() => {
    if (!trendingVisible) {
      trendingTrackedRef.current = false;
      return;
    }
    if (trendingTrackedRef.current) return;
    trendingTrackedRef.current = true;
    session.getManager(TrackingManager).trackSearchEvent('results', 'navbar-search', {
      trending_results: [
        ...trendingTopics.map(topic => ({
          name: topic.name,
          taxonomy: topic.taxonomy,
          url_public: topic.url_public,
        })),
        ...trendingTickers.map(ticker => ({
          name: ticker.symbol ?? '',
          taxonomy: 'tickers',
          url_public: formatTickerToURLFriendly(ticker.symbol),
        })),
      ],
    });
  }, [trendingVisible, session, trendingTopics, trendingTickers]);

  const resetSearchResults = useCallback(() => {
    setFoundedTickers([]);
    setFoundedArticles([]);
  }, []);

  const fetchSearch = useCallback(
    async (searchQuery: string, includeArticles: boolean, includeSymbols: boolean) => {
      try {
        const trimmed = searchQuery.trim();
        const basicNewsManager = session.getManager(BasicNewsManager);
        const searchRes = await basicNewsManager.getSearchResults({
          include_articles: includeArticles,
          include_symbols: includeSymbols,
          page: 1,
          page_size: 20,
          query: trimmed,
          return_limit: MAX_SEARCH_RESULTS_DISPLAY,
          symbol_limit: MAX_SEARCH_RESULTS_DISPLAY,
        });

        const response = searchRes.err ? null : searchRes.ok;
        const articles: SearchResultItem[] = response?.results?.articles ?? [];
        const symbolResults: SearchResultSymbolItem[] = (response?.results?.symbols ?? []).slice(
          0,
          MAX_SEARCH_RESULTS_DISPLAY,
        );

        const tickers: Ticker[] = symbolResults.map(symbol => ({
          exchange: symbol.exchange,
          logo: symbol.logo ?? null,
          name: symbol.name,
          shortName: symbol.shortName ?? '',
          symbol: symbol.symbol,
        })) as Ticker[];

        if (includeArticles && articles.length > 0) {
          session.getManager(TrackingManager).trackSearchEvent('search_results', 'article', {
            query: searchQuery,
            query_results: articles.map((node, idx) => ({
              article_id: node.NodeID,
              relevancy_score: node?.score ?? 0,
              search_rank: idx + 1,
              search_results_page: 1,
            })),
          });
        }

        return { articles, tickers };
      } catch (error) {
        console.error('Error fetching search results:', error);
        return { articles: [], tickers: [] as Ticker[] };
      }
    },
    [session],
  );

  const fetchInfo = React.useCallback(
    (searchQuery: string) => {
      latestQueryRef.current = searchQuery;

      const trimmedLength = searchQuery.trim().length;
      const shouldSearchTickers = tickersSearch && trimmedLength >= 1;
      const shouldSearchArticles = articlesSearch && trimmedLength >= MIN_ARTICLE_SEARCH_QUERY_LENGTH;

      if (!shouldSearchTickers && !shouldSearchArticles) {
        if (fetchDebounce) {
          clearTimeout(fetchDebounce);
        }
        resetSearchResults();
        setIsFetching(false);
        setPendingEnter(false);
        if (onSearching) {
          onSearching(false);
        }
        return;
      }

      if (fetchDebounce) {
        clearTimeout(fetchDebounce);
      }

      setIsFetching(true);
      if (onSearching) {
        onSearching(true);
      }

      const fetchDebounceTimeout = window.setTimeout(async () => {
        try {
          const { articles, tickers } = await fetchSearch(searchQuery, shouldSearchArticles, shouldSearchTickers);
          if (latestQueryRef.current !== searchQuery) {
            return;
          }
          setFoundedTickers(shouldSearchTickers ? tickers : []);
          setFoundedArticles(shouldSearchArticles ? articles : []);
          if (tickers.length > 0) {
            session.getManager(TrackingManager).trackSearchEvent('search', 'debounce', {
              query: searchQuery,
              symbol: tickers.map(ticker => ticker.symbol).join(','),
            });
          }

          setSelectedIndex(0);
        } catch (error) {
          console.error('Error performing debounced search:', error);
        } finally {
          if (latestQueryRef.current === searchQuery) {
            setIsFetching(false);
            if (onSearching) {
              onSearching(false);
            }
          }
        }
      }, 250);

      setFetchDebounce(fetchDebounceTimeout);
    },
    [articlesSearch, fetchDebounce, fetchSearch, onSearching, resetSearchResults, tickersSearch, session],
  );

  const onInput = useCallback(
    (event: React.InputEvent<HTMLInputElement>) => {
      const query = event.currentTarget.value;
      setSearchQuery(query);
      setSelectedIndex(-1);
      fetchInfo(query);
    },
    [fetchInfo],
  );

  const onFocus = useCallback(() => {
    onActivate?.();
    setShowDropdown(true);
    setHasOpened(true);
    if (showTrending) ensureLoaded();
    session.getManager(TrackingManager).trackSearchEvent('focus', '', { query: searchQuery });
  }, [onActivate, session, searchQuery, showTrending, ensureLoaded]);

  const onBlur = useCallback(() => {
    setSearchResultsHeight('auto');
    session.getManager(TrackingManager).trackSearchEvent('blur', '', { query: searchQuery });
  }, [session, searchQuery]);

  const handleClickCancel = useCallback(() => {
    onToggle && onToggle(false);
    session.getManager(TrackingManager).trackSearchEvent('cancel', '', { query: searchQuery });
    setShowDropdown(false);
    setSearchQuery('');
  }, [onToggle, session, searchQuery]);

  const focusOnSearchBar = () => {
    searchBarRef.current?.focus({ preventScroll: true });
  };

  const focusSearchElementByIndex = (index: number, elements: NodeListOf<Element>) => {
    const targetElement = elements[index] as HTMLElement;
    targetElement?.focus({ preventScroll: true });
    setSelectedIndex(index);
  };

  const selectTicker = useCallback(
    (ticker: Ticker) => {
      if (ticker.symbol) {
        session.getManager(TrackingManager).trackSearchEvent('results', '', {
          query: searchQuery,
          symbol: ticker.symbol,
        });
      }
      if (onSelect) {
        onSelect(ticker);
        resetSearchResults();
        setSearchQuery('');
        focusOnSearchBar();
      } else {
        if (!isFetching) {
          router.push(`/quote/${formatTickerToURLFriendly(ticker.symbol)}`);
          setShowDropdown(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelect, resetSearchResults, searchQuery, isFetching],
  );

  const selectableItems = useMemo<SelectableItem[]>(() => {
    if (searchQuery) {
      return [
        ...(tickersSearch ? foundedTickers : []).map(ticker => ({ ticker, type: 'ticker' as const })),
        ...(articlesSearch ? foundedArticles : []).map(article => ({
          href: article.ArticleURL,
          type: 'link' as const,
        })),
        ...(keywordsSearch ? [{ href: `/search?q=${encodeURIComponent(searchQuery)}`, type: 'link' as const }] : []),
      ];
    }
    if (hasTrending) {
      return [
        ...trendingTickers.map(ticker => ({ ticker, type: 'ticker' as const })),
        ...trendingTopics.map(topic => ({
          href: `/search?q=${encodeURIComponent(topic.name)}`,
          type: 'link' as const,
        })),
      ];
    }
    return [];
  }, [
    articlesSearch,
    foundedArticles,
    foundedTickers,
    hasTrending,
    keywordsSearch,
    searchQuery,
    tickersSearch,
    trendingTickers,
    trendingTopics,
  ]);

  const navigateTo = useCallback(
    (href: string) => {
      setShowDropdown(false);
      if (isAbsoluteUrl(href)) {
        window.location.href = href;
      } else {
        router.push(href);
      }
    },
    [router],
  );

  const commitSelection = useCallback(
    (index: number): boolean => {
      const item = selectableItems[index];
      if (!item) return false;
      if (item.type === 'ticker') {
        selectTicker(item.ticker);
      } else {
        navigateTo(item.href);
      }
      return true;
    },
    [navigateTo, selectableItems, selectTicker],
  );

  useEffect(() => {
    if (!pendingEnter || isFetching) return;
    setPendingEnter(false);
    const index = selectedIndex >= 0 ? selectedIndex : searchQuery ? 0 : -1;
    if (!commitSelection(index) && searchQuery) {
      navigateTo(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  }, [commitSelection, isFetching, navigateTo, pendingEnter, searchQuery, selectedIndex]);

  const onSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLAnchorElement>) => {
      const { key } = event;

      if (key === 'Escape') {
        setShowDropdown(false);
        setSelectedIndex(-1);
      } else if (key === 'Enter') {
        event.preventDefault();
        if (isFetching) {
          setPendingEnter(true);
          return;
        }
        const index = selectedIndex >= 0 ? selectedIndex : searchQuery ? 0 : -1;
        if (!commitSelection(index) && searchQuery) {
          navigateTo(`/search?q=${encodeURIComponent(searchQuery)}`);
        }
      } else if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();

        const elements = ref.current?.querySelectorAll('.' + selectElementListClass);
        if (!elements || elements.length === 0) return;

        const lastIndex = elements.length - 1;
        const nextIndex =
          key === 'ArrowDown'
            ? selectedIndex + 1 > lastIndex
              ? 0
              : selectedIndex + 1
            : selectedIndex - 1 < 0
              ? lastIndex
              : selectedIndex - 1;

        focusSearchElementByIndex(nextIndex, elements);
      } else if (key === 'Backspace') {
        focusOnSearchBar();
      } else {
        setShowDropdown(true);
        setSelectedIndex(-1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commitSelection, isFetching, navigateTo, searchQuery, selectedIndex],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      onSearchKeyDown(e);
    },
    [onSearchKeyDown],
  );

  const handleOpenFullScreenSearch = () => {
    if (isFullScreenSearch) return;
    onToggle && onToggle(true);
  };

  const hasQuery = !!searchQuery;
  const showAutocomplete = !isFetching && hasQuery && showDropdown;
  const showTrendingDropdown = trendingVisible;
  const isDropdownOpen = !dropdownSuppressed && (showAutocomplete || showTrendingDropdown);

  useEffect(() => {
    if (dropdownSuppressed) {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [dropdownSuppressed]);
  const dropdownOpenClass = isDropdownOpen ? ' searchbar-dropdown-open' : '';

  const isSearchActive = hasQuery && showDropdown;
  const showOverlay =
    showBackdrop &&
    !dropdownSuppressed &&
    (isSearchActive || showTrendingDropdown) &&
    !isFullScreenSearch &&
    typeof document !== 'undefined';

  return (
    <>
      {showOverlay
        ? createPortal(
            <div
              aria-hidden="true"
              className={`${styles.backdrop} search-backdrop`}
              onMouseDown={() => {
                setShowDropdown(false);
                setSelectedIndex(-1);
              }}
            />,
            document.body,
          )
        : null}
      <div
        className={`${styles.wrapper} searchbar-wrapper searchbar-${variant}${dropdownOpenClass}`}
        onClick={handleOpenFullScreenSearch}
        ref={ref}
      >
        <div className={`${styles.container} searchbar-container${dropdownOpenClass}`}>
          <SearchBar
            autoFocus={autoFocus}
            handleClickCancel={handleClickCancel}
            handleRemove={handleRemove}
            inputRef={searchBarRef}
            isFullScreenSearch={isFullScreenSearch}
            onBlur={onBlur}
            onFocus={onFocus}
            onInput={onInput}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            searchQuery={searchQuery}
            selected={selected}
          />
          {isDropdownOpen ? (
            <div className={`${styles.dropdown} search-dropdown`}>
              <div className={`${styles.resultWrapper} search-result-wrapper`} style={{ height: searchResultsHeight }}>
                {showAutocomplete && (
                  <AutocompleteResults
                    foundedArticles={articlesSearch ? foundedArticles : []}
                    foundedTickers={tickersSearch ? foundedTickers : []}
                    onKeyDown={onSearchKeyDown}
                    onTickerClick={selectTicker}
                    searchQuery={searchQuery}
                    selectedIndex={selectedIndex}
                    selectElementListClass={selectElementListClass}
                    showKeywordsSearch={keywordsSearch}
                  />
                )}
                {showTrendingDropdown && (
                  <TrendingSections
                    loading={trendingLoading}
                    onKeyDown={onSearchKeyDown}
                    selectedIndex={selectedIndex}
                    selectElementListClass={selectElementListClass}
                    trendingTickers={trendingTickers}
                    trendingTopics={trendingTopics}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

interface ResultListItemInterface {
  logo?: string;
  change?: number;
  changePercent?: number;
  className?: string;
  date?: string;
  dateElement?: React.ReactElement;
  descriptionElement?: React.ReactElement;
  description?: string;
  hidePriceInfo?: boolean;
  link?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLAnchorElement>;
  price?: number;
  subtitle?: string;
  title: string;
}

const ListItem: React.FC<ResultListItemInterface> = ({
  className,
  date,
  dateElement,
  description,
  descriptionElement,
  link,
  logo,
  onClick,
  onKeyDown,
  subtitle,
  title,
}) => (
  <a
    className={`${styles.listItemWrapper} ${className ?? ''}`.trim()}
    href={link}
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={-1}
  >
    <div className={styles.listItemInfo}>
      {logo !== undefined ? (
        logo ? (
          <div
            className={styles.listItemLogoImage}
            style={logo ? { backgroundImage: `url(${logo})` } : { backgroundColor: '#fff' }}
          />
        ) : (
          <span className={styles.listItemLogo}>
            <FallbackLogoSVG letter={title?.charAt(0) ?? ''} />
          </span>
        )
      ) : null}
      <div>
        {subtitle && (
          <span className={styles.listItemSubtitle} dangerouslySetInnerHTML={{ __html: sanitizeHTML(subtitle) }} />
        )}
        <div className={styles.listItemTitle} dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
        {description && (
          <small
            className={styles.listItemDescription}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
          />
        )}
        {descriptionElement && descriptionElement}
      </div>
    </div>
    {dateElement ?? (date && <small className={styles.listItemDate}>{date}</small>)}
  </a>
);

export const ResultListItem: React.FC<ResultListItemInterface> = props => (
  <ListItem {...props} className={`${styles.resultListItem} ${props.className ?? ''}`.trim()} />
);
